import React, { useCallback, useEffect, useId, useRef, useState } from 'react'

import clsx from 'clsx'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import './ApplyV2DatePickerInput.scss'

dayjs.extend(isoWeek)
dayjs.locale(document?.documentElement?.lang ?? 'en')

const ClearIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

interface Props {
    value?: string | null | undefined // DD-MM-YYYY
    valueFormat?: 'DD-MM-YYYY' | 'YYYY-MM-DD' | 'auto'
    displayFormat?: string
    displayLocale?: string
    onChange?: (
        value: string | null | undefined,
        objValue?: { day: number | string; month: number | string; year: number | string } | null | undefined
    ) => void
    label?: string
    errorMessage?: string | null | boolean | React.ReactNode
    errors?: {
        day: number | string | null | undefined | boolean
        month: number | string | null | undefined | boolean
        year: number | string | null | undefined | boolean
    }
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isRequired?: boolean
    isShowErrorMessage?: boolean
    isLoading?: boolean
    description?: React.ReactNode
    isClearable?: boolean
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    placeholder?: string
    minDate?: string // DD-MM-YYYY
    maxDate?: string // DD-MM-YYYY
}

const baseClass = 'ApplyV2DatePickerInput'

function ApplyV2DatePickerInput({
    value,
    valueFormat = 'DD-MM-YYYY',
    displayFormat = 'DD-MM-YYYY',
    displayLocale,
    onChange,
    onFocus,
    onBlur,
    label,
    errorMessage,
    isDisabled = false,
    id,
    name,
    isRequired = false,
    isShowErrorMessage = true,
    isLoading = false,
    description,
    isClearable = true,
    placeholder = 'DD-MM-YYYY',
    className,
    minDate,
    maxDate,
    errors,
}: Props) {
    const defaultId = useId()
    const { getTitle } = useApplyV2NavTranslations()
    const [isOpen, setIsOpen] = useState(false)
    const [isInputFocused, setIsInputFocused] = useState(false)
    const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom')
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(() => {
        if (!value) return null
        if (valueFormat === 'auto') {
            if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
                return dayjs(value, 'DD-MM-YYYY', true)
            } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return dayjs(value, 'YYYY-MM-DD', true)
            }
            return dayjs(value)
        }

        return dayjs(value, valueFormat, true)
    })
    const [currentMonth, setCurrentMonth] = useState(dayjs())

    const mainRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    const displayValue = selectedDate
        ? (displayLocale ? selectedDate.locale(displayLocale) : selectedDate).format(displayFormat)
        : ''

    const formatSelectedDateForDisplay = useCallback(
        (date: dayjs.Dayjs) => (displayLocale ? date.locale(displayLocale) : date).format(displayFormat),
        [displayFormat, displayLocale]
    )

    const currentIsoWeek = dayjs().isoWeek()
    const currentIsoYear = dayjs().isoWeekYear()

    const errorMessageTotal =
        errorMessage || [...new Set([errors?.day, errors?.month, errors?.year].filter(Boolean))].join(', ')

    const parseDate = useCallback(
        (dateString: string | null | undefined) => {
            if (!dateString) return null

            if (valueFormat === 'auto') {
                if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
                    return dayjs(dateString, 'DD-MM-YYYY', true)
                } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                    return dayjs(dateString, 'YYYY-MM-DD', true)
                }
                return dayjs(dateString)
            }

            return dayjs(dateString, valueFormat, true)
        },
        [valueFormat]
    )

    const getMonthTranslation = useCallback(
        (mnth: string) => {
            const vls: Record<string, string> = {
                '1': getTitle('477', 'January'),
                '2': getTitle('478', 'February'),
                '3': getTitle('479', 'March'),
                '4': getTitle('480', 'April'),
                '5': getTitle('481', 'May'),
                '6': getTitle('482', 'June'),
                '7': getTitle('483', 'July'),
                '8': getTitle('484', 'August'),
                '9': getTitle('485', 'September'),
                '10': getTitle('486', 'October'),
                '11': getTitle('487', 'November'),
                '12': getTitle('488', 'December'),
            }
            return vls[mnth]
        },
        [getTitle]
    )

    useEffect(() => {
        if (selectedDate) {
            setCurrentMonth(selectedDate.startOf('month'))
        }
    }, [selectedDate])

    useEffect(() => {
        if (value) {
            const date = parseDate(value)
            if (date?.isValid()) {
                setSelectedDate(date)
            }
        } else {
            setSelectedDate(null)
        }
    }, [value, parseDate])

    const generateCalendar = () => {
        const startOfMonth = currentMonth.startOf('month')
        const endOfMonth = currentMonth.endOf('month')

        let startDay = startOfMonth.startOf('month')
        while (startDay.day() !== 1) {
            startDay = startDay.subtract(1, 'day')
        }

        let endDay = endOfMonth.endOf('month')
        while (endDay.day() !== 0) {
            // 0 = неділя
            endDay = endDay.add(1, 'day')
        }

        const weeks: dayjs.Dayjs[][] = []
        let currentWeek: dayjs.Dayjs[] = []
        let currentDate = startDay

        while (currentDate.isBefore(endDay) || currentDate.isSame(endDay, 'day')) {
            currentWeek.push(currentDate)

            if (currentWeek.length === 7) {
                weeks.push(currentWeek)
                currentWeek = []
            }

            currentDate = currentDate.add(1, 'day')
        }

        if (currentWeek.length > 0) {
            weeks.push(currentWeek)
        }

        return weeks
    }

    const weeks = generateCalendar()

    const handleDateSelect = (date: dayjs.Dayjs) => {
        const formattedDate = date.format(valueFormat)
        setSelectedDate(date)
        onChange?.(formattedDate, { day: date.format('DD'), month: date.format('MM'), year: date.format('YYYY') })
        setIsOpen(false) // TODO - remove

        if (inputRef.current) {
            inputRef.current.value = formattedDate
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        const filteredValue = inputValue.replace(/[^0-9-]/g, '')

        let formattedValue = filteredValue.replace(/-/g, '')

        if (formattedValue.length >= 2) {
            formattedValue = formattedValue.substring(0, 2) + '-' + formattedValue.substring(2)
        }
        if (formattedValue.length >= 5) {
            formattedValue = formattedValue.substring(0, 5) + '-' + formattedValue.substring(5)
        }
        if (formattedValue.length > 10) {
            formattedValue = formattedValue.substring(0, 10)
        }

        if (inputRef.current) {
            inputRef.current.value = formattedValue
        }

        if (formattedValue.replace(/-/g, '').length === 8) {
            const parsedDate = parseDate(formattedValue)
            if (parsedDate?.isValid()) {
                setSelectedDate(parsedDate)
                onChange?.(formattedValue, {
                    day: parsedDate.format('DD'),
                    month: parsedDate.format('MM'),
                    year: parsedDate.format('YYYY'),
                })
            }
        } else if (formattedValue === '') {
            setSelectedDate(null)
            onChange?.(null)
        }
    }

    const updateDateFromValue = (value: string) => {
        const parsedDate = parseDate(value)
        if (parsedDate?.isValid()) {
            setSelectedDate(parsedDate)
            onChange?.(value)
        } else if (value === '') {
            setSelectedDate(null)
            onChange?.(undefined)
        }
    }

    const formatDateFromRaw = (rawValue: string): string => {
        if (rawValue.length === 0) return ''

        let formatted = rawValue
        if (rawValue.length >= 2) {
            formatted = rawValue.substring(0, 2) + '-' + rawValue.substring(2)
        }
        if (rawValue.length >= 4) {
            formatted = formatted.substring(0, 5) + '-' + formatted.substring(5)
        }
        if (formatted.length > 10) {
            formatted = formatted.substring(0, 10)
        }
        return formatted
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const input = inputRef.current
        if (!input) return

        const currentValue = input.value
        const selectionStart = input.selectionStart || 0
        const selectionEnd = input.selectionEnd || 0

        const getRawCursorPosition = (formattedPosition: number, value: string): number => {
            let rawPosition = 0
            for (let i = 0; i < Math.min(formattedPosition, value.length); i++) {
                if (value[i] !== '-') {
                    rawPosition++
                }
            }
            return rawPosition
        }
        const getFormattedCursorPosition = (rawPosition: number, value: string): number => {
            if (rawPosition === 0) return 0

            let formattedPosition = 0
            let rawCount = 0

            for (let i = 0; i < value.length; i++) {
                formattedPosition++
                if (value[i] !== '-') {
                    rawCount++
                    if (rawCount === rawPosition) {
                        break
                    }
                }
            }
            return formattedPosition
        }

        const allowedKeys =
            /^[0-9]$|^Backspace$|^Delete$|^Tab$|^ArrowLeft$|^ArrowRight$|^ArrowUp$|^ArrowDown$|^Enter$|^Escape$/

        if (!allowedKeys.test(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault()
            return
        }

        if (e.key === 'Enter') {
            const inputValue = input.value || ''
            const parsedDate = parseDate(inputValue)

            if (parsedDate?.isValid()) {
                setSelectedDate(parsedDate)
                onChange?.(parsedDate.format(valueFormat), {
                    day: parsedDate.format('DD'),
                    month: parsedDate.format('MM'),
                    year: parsedDate.format('YYYY'),
                })
            }
            setIsOpen(false)
        } else if (e.key === 'Escape') {
            setIsOpen(false)
        } else if (/^[0-9]$/.test(e.key)) {
            e.preventDefault()

            const rawValue = currentValue.replace(/[^0-9]/g, '')
            const newChar = e.key

            let newRawValue = ''
            let newCursorPosition = selectionStart

            if (selectionStart === selectionEnd) {
                newRawValue = rawValue.substring(0, selectionStart) + newChar + rawValue.substring(selectionStart)
                newCursorPosition = selectionStart + 1
            } else {
                newRawValue = rawValue.substring(0, selectionStart) + newChar + rawValue.substring(selectionEnd)
                newCursorPosition = selectionStart + 1
            }

            if (newRawValue.length > 8) {
                return
            }

            let formattedValue = newRawValue

            if (newRawValue.length >= 2) {
                formattedValue = newRawValue.substring(0, 2) + '-' + newRawValue.substring(2)
                if (selectionStart < 2) newCursorPosition += 1
            }

            if (newRawValue.length >= 4) {
                formattedValue = formattedValue.substring(0, 5) + '-' + formattedValue.substring(5)
                if (selectionStart >= 2 && selectionStart < 4) newCursorPosition += 1
            }

            if (formattedValue.length > 10) {
                formattedValue = formattedValue.substring(0, 10)
            }

            input.value = formattedValue

            setTimeout(() => {
                input.setSelectionRange(newCursorPosition + 1, newCursorPosition + 1)
            }, 0)

            if (newRawValue.length === 8) {
                const parsedDate = dayjs(formattedValue, 'DD-MM-YYYY', true)
                if (parsedDate?.isValid()) {
                    setSelectedDate(parsedDate)
                    onChange?.(parsedDate.format(valueFormat), {
                        day: parsedDate.format('DD'),
                        month: parsedDate.format('MM'),
                        year: parsedDate.format('YYYY'),
                    })
                }
            }
        } else if (e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault()

            const rawValue = currentValue.replace(/[^0-9]/g, '')
            const rawSelectionStart = getRawCursorPosition(selectionStart, currentValue)
            const rawSelectionEnd = getRawCursorPosition(selectionEnd, currentValue)

            if (selectionStart === selectionEnd) {
                let newRawValue = ''
                let newRawCursorPosition = rawSelectionStart

                if (e.key === 'Backspace' && rawSelectionStart > 0) {
                    newRawValue = rawValue.substring(0, rawSelectionStart - 1) + rawValue.substring(rawSelectionStart)
                    newRawCursorPosition = rawSelectionStart - 1
                } else if (e.key === 'Delete' && rawSelectionStart < rawValue.length) {
                    newRawValue = rawValue.substring(0, rawSelectionStart) + rawValue.substring(rawSelectionStart + 1)
                    newRawCursorPosition = rawSelectionStart
                } else {
                    return
                }

                const formattedValue = formatDateFromRaw(newRawValue)
                input.value = formattedValue

                const newFormattedCursorPosition = getFormattedCursorPosition(newRawCursorPosition, formattedValue)

                setTimeout(() => {
                    input.setSelectionRange(newFormattedCursorPosition, newFormattedCursorPosition)
                }, 0)

                updateDateFromValue(formattedValue)
            } else {
                const newRawValue = rawValue.substring(0, rawSelectionStart) + rawValue.substring(rawSelectionEnd)

                const formattedValue = formatDateFromRaw(newRawValue)
                input.value = formattedValue

                const newFormattedCursorPosition = getFormattedCursorPosition(rawSelectionStart, formattedValue)

                setTimeout(() => {
                    input.setSelectionRange(newFormattedCursorPosition, newFormattedCursorPosition)
                }, 0)

                updateDateFromValue(formattedValue)
            }
        }
    }

    const handlePrevMonth = () => {
        setCurrentMonth(currentMonth.subtract(1, 'month'))
    }

    const handleNextMonth = () => {
        setCurrentMonth(currentMonth.add(1, 'month'))
    }

    // const handleToday = () => {
    //     const today = dayjs()
    //     setSelectedDate(today)
    //     setCurrentMonth(today.startOf('month'))
    //     onChange?.(today.format('DD-MM-YYYY'), {
    //         day: today.format('DD'),
    //         month: today.format('MM'),
    //         year: today.format('YYYY'),
    //     })
    //     setIsOpen(false) // TODO -remove
    //
    //     if (inputRef.current) {
    //         inputRef.current.value = today.format('DD-MM-YYYY')
    //     }
    // }

    const handleClear = () => {
        setSelectedDate(null)
        onChange?.(null)
        if (inputRef.current) {
            inputRef.current.value = ''
        }
        setIsOpen(false) // TODO -remove
    }

    const calculateMenuPosition = () => {
        if (!mainRef.current || !inputRef.current) return 'bottom'

        const selectRect = inputRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - selectRect.bottom - 20
        const spaceAbove = selectRect.top
        const estimatedMenuHeight = menuRef.current ? menuRef.current.getBoundingClientRect().height : 300
        // const estimatedMenuHeight = menuRef.current ? menuRef.current.getBoundingClientRect().top + spaceBelow : 0

        if (spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) {
            return 'top'
        }

        return 'bottom'
    }

    const updateMenuPosition = () => {
        // if (isOpen) {
        setMenuPosition(calculateMenuPosition())
        // }
    }

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('scroll', updateMenuPosition, true)
            window.addEventListener('resize', updateMenuPosition)
        }
        return () => {
            window.removeEventListener('scroll', updateMenuPosition, true)
            window.removeEventListener('resize', updateMenuPosition)
        }
    }, [isOpen])

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsInputFocused(true)
        if (!isDisabled) {
            setIsOpen(true)
            updateMenuPosition()
        }
        onFocus?.(e)
    }

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsInputFocused(false)
        setTimeout(() => {
            if (!menuRef.current?.contains(document.activeElement)) {
                setIsOpen(false) // TODO -remove

                const inputValue = inputRef.current?.value || ''
                if (inputValue) {
                    const parsedDate =
                        dayjs(inputValue, displayFormat, true).isValid()
                            ? dayjs(inputValue, displayFormat, true)
                            : dayjs(inputValue, 'DD-MM-YYYY', true)
                    if (!parsedDate.isValid()) {
                        if (inputRef.current) {
                            inputRef.current.value = selectedDate ? formatSelectedDateForDisplay(selectedDate) : ''
                        }
                    } else {
                        setSelectedDate(parsedDate)
                        onChange?.(parsedDate.format(valueFormat), {
                            day: parsedDate.format('DD'),
                            month: parsedDate.format('MM'),
                            year: parsedDate.format('YYYY'),
                        })
                    }
                }
            }
        }, 100)
        onBlur?.(e)
    }

    const handleModalClick = (e: React.MouseEvent) => {
        e.stopPropagation()
    }

    const handleModalBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (!mainRef.current?.contains(e.relatedTarget as Node)) {
            setIsOpen(false) // TODO -remove
        }
    }

    const weekDays = [
        '',
        getTitle('11044', 'Mo'),
        getTitle('11045', 'Tu'),
        getTitle('11046', 'We'),
        getTitle('11047', 'Th'),
        getTitle('11048', 'Fr'),
        getTitle('11049', 'Sa'),
        getTitle('11050', 'Su'),
    ]

    const isDateDisabled = (date: dayjs.Dayjs) => {
        if (minDate) {
            const min = dayjs(minDate, 'DD-MM-YYYY', true)
            if (min.isValid() && date.isBefore(min, 'day')) {
                return true
            }
        }
        if (maxDate) {
            const max = dayjs(maxDate, 'DD-MM-YYYY', true)
            if (max.isValid() && date.isAfter(max, 'day')) {
                return true
            }
        }
        return false
    }

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessageTotal,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
                [`${baseClass}--open`]: isOpen,
                [`${baseClass}--focused`]: isInputFocused,
            })}
            ref={mainRef}
            data-error={!!errorMessage ? 'true' : 'false'}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : defaultId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}
            <div
                className={clsx(`${baseClass}__wrapper`, {
                    [`${baseClass}__wrapper--disabled`]: isDisabled,
                    [`${baseClass}__wrapper--loading`]: isLoading,
                    [`${baseClass}__wrapper--error`]: !!errorMessageTotal,
                    [`${baseClass}__wrapper--open`]: isOpen,
                    [`${baseClass}__wrapper--focused`]: isInputFocused,
                    [`${baseClass}__wrapper--has-value`]: !!selectedDate,
                })}
            >
                <input
                    ref={inputRef}
                    type="text"
                    inputMode={'numeric'}
                    id={id ? id.toString() : defaultId}
                    name={name}
                    value={displayValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onKeyDown={handleInputKeyDown}
                    disabled={isDisabled}
                    placeholder={placeholder ?? valueFormat}
                    autoComplete="off"
                    className={clsx(`${baseClass}__input`, {
                        [`${baseClass}__input--error`]: !!errorMessageTotal,
                        [`${baseClass}__input--loading`]: isLoading,
                        [`${baseClass}__input--focused`]: isInputFocused,
                        [`${baseClass}__input--has-value`]: !!selectedDate,
                    })}
                />

                {isClearable && selectedDate && !isDisabled && !isLoading && (
                    <div className={`${baseClass}__input-clear-wrapper`}>
                        <button
                            type="button"
                            className={`${baseClass}__input-clear-button`}
                            onClick={handleClear}
                            aria-label="Clear date"
                            tabIndex={-1}
                        >
                            <ClearIcon />
                        </button>
                    </div>
                )}

                {isOpen && (
                    <div
                        ref={menuRef}
                        className={clsx(`${baseClass}__modal`, {
                            [`${baseClass}__modal--isOpen`]: isOpen,
                            [`${baseClass}__modal--focused`]: isInputFocused,
                            [`${baseClass}__modal--top`]: menuPosition === 'top',
                        })}
                        // style={menuPosition === 'top' ? { bottom: '50px', top: 'auto' } : undefined}
                        onClick={handleModalClick}
                        onBlur={handleModalBlur}
                        tabIndex={-1}
                    >
                        <div className={`${baseClass}__calendar`}>
                            <div className={`${baseClass}__calendar-header`}>
                                <div className={`${baseClass}__calendar-month`}>
                                    {getMonthTranslation(currentMonth.format('M'))} {currentMonth.format('YYYY')}
                                </div>
                                <button
                                    type="button"
                                    className={`${baseClass}__calendar-nav-button ${baseClass}__calendar-nav-button--prev`}
                                    onClick={handlePrevMonth}
                                    aria-label="Previous month"
                                    tabIndex={-1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M10.7425 13.4387C10.9847 13.2126 10.9978 12.833 10.7717 12.5907L6.48717 8.0001L10.7717 3.40949C10.9978 3.16724 10.9847 2.78756 10.7425 2.56146C10.5002 2.33536 10.1206 2.34845 9.89447 2.5907L5.2278 7.5907C5.01265 7.82123 5.01265 8.17896 5.2278 8.40949L9.89447 13.4095C10.1206 13.6517 10.5002 13.6648 10.7425 13.4387Z"
                                            fill="#77777C"
                                        />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    className={`${baseClass}__calendar-nav-button ${baseClass}__calendar-nav-button--next`}
                                    onClick={handleNextMonth}
                                    aria-label="Next month"
                                    tabIndex={-1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M5.25751 13.4387C5.01526 13.2126 5.00216 12.833 5.22826 12.5907L9.51283 8.0001L5.22826 3.40949C5.00216 3.16724 5.01526 2.78756 5.25751 2.56146C5.49976 2.33536 5.87943 2.34845 6.10553 2.5907L10.7722 7.5907C10.9874 7.82123 10.9874 8.17896 10.7722 8.40949L6.10553 13.4095C5.87943 13.6517 5.49976 13.6648 5.25751 13.4387Z"
                                            fill="#77777C"
                                        />
                                    </svg>
                                </button>
                            </div>

                            {/*<div className={`${baseClass}__calendar-today`}>*/}
                            {/*    <button*/}
                            {/*        type="button"*/}
                            {/*        className={`${baseClass}__calendar-today-button`}*/}
                            {/*        onClick={handleToday}*/}
                            {/*    >*/}
                            {/*        Today*/}
                            {/*    </button>*/}
                            {/*    {isClearable && (*/}
                            {/*        <button*/}
                            {/*            type="button"*/}
                            {/*            className={`${baseClass}__calendar-clear-button`}*/}
                            {/*            onClick={handleClear}*/}
                            {/*        >*/}
                            {/*            Clear*/}
                            {/*        </button>*/}
                            {/*    )}*/}
                            {/*</div>*/}

                            <div className={`${baseClass}__calendar-weekdays`}>
                                {weekDays.map((day, index) => (
                                    <div
                                        key={index}
                                        className={clsx(`${baseClass}__calendar-weekday`, {
                                            // [`${baseClass}__calendar-week-number`]: index === 0,
                                        })}
                                    >
                                        {day}
                                    </div>
                                ))}
                            </div>

                            <div className={`${baseClass}__calendar-grid`}>
                                {weeks.map((week, weekIndex) => {
                                    const weekStart = week[0]
                                    const isCurrentWeek =
                                        weekStart.isoWeek() === currentIsoWeek &&
                                        weekStart.isoWeekYear() === currentIsoYear
                                    return (
                                        <div key={weekIndex} className={`${baseClass}__calendar-week`}>
                                            {/*<div className={`${baseClass}__calendar-week-number`}>{weekIndex + 1}</div>*/}
                                            <div
                                                className={clsx(`${baseClass}__calendar-week-number`, {
                                                    [`${baseClass}__calendar-week-number--current`]: isCurrentWeek,
                                                })}
                                            >
                                                {weekStart.isoWeek()}
                                            </div>
                                            {week.map((day, dayIndex) => {
                                                const isCurrentMonth = day.month() === currentMonth.month()
                                                const isSelected = selectedDate && day.isSame(selectedDate, 'day')
                                                const isToday = day.isSame(dayjs(), 'day')
                                                const isDateDisabledFlag = isDateDisabled(day)

                                                return (
                                                    <button
                                                        key={dayIndex}
                                                        type="button"
                                                        className={clsx(`${baseClass}__calendar-day`, {
                                                            [`${baseClass}__calendar-day--other-month`]:
                                                                !isCurrentMonth,
                                                            [`${baseClass}__calendar-day--selected`]: isSelected,
                                                            [`${baseClass}__calendar-day--today`]: isToday,
                                                            [`${baseClass}__calendar-day--disabled`]:
                                                                isDateDisabledFlag,
                                                        })}
                                                        onClick={() => !isDateDisabledFlag && handleDateSelect(day)}
                                                        disabled={isDateDisabledFlag}
                                                        aria-label={`Вибрати ${day.format('DD-MM-YYYY')}`}
                                                        aria-selected={isSelected ?? undefined}
                                                        tabIndex={-1}
                                                    >
                                                        <div
                                                            className={clsx(`${baseClass}__calendar-day-label`, {
                                                                [`${baseClass}__calendar-day-label--other-month`]:
                                                                    !isCurrentMonth,
                                                                [`${baseClass}__calendar-day-label--selected`]:
                                                                    isSelected,
                                                                [`${baseClass}__calendar-day-label--today`]: isToday,
                                                                [`${baseClass}__calendar-day-label--disabled`]:
                                                                    isDateDisabledFlag,
                                                            })}
                                                        >
                                                            {day.date()}
                                                        </div>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {description && <div className={`${baseClass}__description`}>{description}</div>}
            {errorMessageTotal && isShowErrorMessage && (
                <div className={`${baseClass}__error`}>
                    <div className={`${baseClass}__error-icon`}>
                        <WarningErrorIcon />
                    </div>
                    <div className={`${baseClass}__error-text`}>{errorMessageTotal}</div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2DatePickerInput
